import Cookies from "js-cookie";

// Google Analytics 4 for the whole site.
// - one page_view per page (sent here on route changes, not by gtag itself);
// - addresses are cleaned before they reach Google: links with personal
//   tokens (release, invoice, upload links) become /release/:token, and only
//   campaign / search parameters stay in the query;
// - no emails, names, passwords or tokens in any event;
// - Europe (GDPR): nothing loads until the visitor accepts analytics cookies.

export const GA_ID = "G-0YMMGV7ED2";
const CONSENT_KEY = "vb_analytics_consent"; // "granted" | "denied"
export const CONSENT_EVENT = "vb-analytics-consent";

// EEA, UK and Switzerland: Google's own region list for the consent default
const CONSENT_REGIONS = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IS", "IE", "IT", "LV", "LI", "LT",
  "LU", "MT", "NL", "NO", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "GB", "CH",
];

window.dataLayer = window.dataLayer || [];
function gtag() {
  // gtag.js reads the arguments object itself
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const storage = {
  get: () => {
    try {
      return localStorage.getItem(CONSENT_KEY);
    } catch (e) {
      return null;
    }
  },
  set: (v) => {
    try {
      localStorage.setItem(CONSENT_KEY, v);
    } catch (e) {
      // private mode: the choice lasts for this page load
    }
  },
};

// visitors in Europe are asked first (time zone: no request to a geo service)
const inEurope = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return /^Europe\//.test(tz) || /^Atlantic\/(Reykjavik|Canary|Madeira|Azores|Faroe)$/.test(tz);
  } catch (e) {
    return false;
  }
};

export const consentNeeded = () => inEurope() && !storage.get();
export const consentChoice = () => storage.get();

/* ─── Clean addresses ───────────────────────────── */

const SECRET_PATHS = [
  [/^\/release-request\/[^/]+/, "/release-request/:token"],
  [/^\/release\/[^/]+/, "/release/:token"],
  [/^\/invoice\/[^/]+/, "/invoice/:token"],
  [/^\/partner-invoice\/[^/]+/, "/partner-invoice/:token"],
  [/^\/submitVideos\/[^/]+/, "/submitVideos/:link"],
  [/^\/uploadVideos\/[^/]+\/[^/]+/, "/uploadVideos/:code/:link"],
  [/^\/submittedVideo\/[^/]+/, "/submittedVideo/:code"],
  [/^\/videoLink\/[^/]+/, "/videoLink/:link"],
  [/^\/licence\/[^/]+/, "/licence/:token"],
  [/^\/unsubscribe\/[^/]+/, "/unsubscribe/:token"],
];
// campaign tags (where visitors come from) and library searches
const KEEP_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "q", "tag", "category"];

export const cleanPath = (pathname) => {
  for (const [re, to] of SECRET_PATHS) if (re.test(pathname)) return pathname.replace(re, to);
  return pathname;
};

const cleanUrl = (loc = window.location) => {
  const params = new URLSearchParams(loc.search);
  const kept = new URLSearchParams();
  KEEP_PARAMS.forEach((k) => params.has(k) && kept.set(k, params.get(k).slice(0, 100)));
  const q = kept.toString();
  return `${loc.origin}${cleanPath(loc.pathname)}${q ? `?${q}` : ""}`;
};

// the site's own pages as referrer are cleaned the same way
const cleanReferrer = () => {
  try {
    const r = document.referrer ? new URL(document.referrer) : null;
    if (!r) return "";
    if (r.origin !== window.location.origin) return `${r.origin}/`;
    return cleanUrl(r);
  } catch (e) {
    return "";
  }
};

/* ─── Who is browsing ───────────────────────────── */

const roleFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.role || "";
  } catch (e) {
    return "";
  }
};

// guest · partner (media company account) · admin (VBear team) · author
export const userType = () => {
  if (Cookies.get("vb-client-token")) return "partner";
  const staff = Cookies.get("vb-access-token");
  if (staff) return roleFromToken(staff) === "author" ? "author" : "admin";
  return "guest";
};

/* ─── Loading ───────────────────────────────────── */

let loaded = false;
let started = false;

const loadScript = () => {
  if (loaded) return;
  loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
};

const debugMode = () => {
  try {
    return /[?&]ga_debug=1\b/.test(window.location.search) || localStorage.getItem("vb_ga_debug") === "1";
  } catch (e) {
    return false;
  }
};

export const initAnalytics = () => {
  if (started) return;
  started = true;
  if (debugMode()) {
    try {
      localStorage.setItem("vb_ga_debug", "1"); // stays on for the next pages
    } catch (e) {}
  }
  // no ads: ad storage is off everywhere; analytics off by default in Europe
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  });
  gtag("consent", "default", { analytics_storage: "denied", region: CONSENT_REGIONS });
  if (storage.get() === "granted") gtag("consent", "update", { analytics_storage: "granted" });

  gtag("js", new Date());
  gtag("config", GA_ID, {
    send_page_view: false, // page views are sent by trackPage, once per page
    page_location: cleanUrl(),
    page_referrer: cleanReferrer(),
    ...(debugMode() && { debug_mode: true }),
  });
  gtag("set", "user_properties", { user_type: userType() });

  // Europe: the script waits for the visitor's choice
  if (!inEurope() || storage.get() === "granted") loadScript();
};

export const setConsent = (granted) => {
  storage.set(granted ? "granted" : "denied");
  if (granted) {
    gtag("consent", "update", { analytics_storage: "granted" });
    loadScript();
  }
  window.dispatchEvent(new Event(CONSENT_EVENT));
};

/* ─── Page views and events ─────────────────────── */

// /library/123/some-title and /library/123 are one page (the slug redirect)
const pageKey = (pathname) => cleanPath(pathname).replace(/^(\/library\/\d+)\/.*$/, "$1").replace(/\/+$/, "") || "/";
let lastPage = "";

// the same events for our own log of signed-in clients (clientSession.js)
export const TRACK_EVENT = "vb-track";
const relay = (name, params) => {
  try {
    window.dispatchEvent(new CustomEvent(TRACK_EVENT, { detail: { name, params } }));
  } catch (e) {
    // old browser: Google only
  }
};

export const trackPage = (loc = window.location) => {
  const key = pageKey(loc.pathname);
  if (key === lastPage) return; // filters and slug redirects change only the query / slug
  lastPage = key;
  relay("page_view", { path: key });
  // the address now; the title once the page has rendered and loaded its
  // data (react-helmet sets it a moment later)
  const page_location = cleanUrl();
  // the new page for every following event (video_view, license_click…)
  gtag("config", GA_ID, { send_page_view: false, page_location, ...(debugMode() && { debug_mode: true }) });
  setTimeout(() => {
    const here = lastPage === key; // still on this page: its own title is set
    const page_title = here ? document.title : "";
    if (here) gtag("config", GA_ID, { send_page_view: false, page_location: cleanUrl(), page_title, ...(debugMode() && { debug_mode: true }) });
    gtag("event", "page_view", clean({ page_location: here ? cleanUrl() : page_location, page_title, user_type: userType() }));
  }, 1200);
};

const clean = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    // GA limits: page_location 1000, page_title 300, other values 100
    const max = k === "page_location" ? 1000 : k === "page_title" ? 300 : 100;
    out[k] = typeof v === "string" ? v.slice(0, max) : v;
  });
  return out;
};

export const track = (name, params = {}) => {
  // the current page, cleaned (the page may have changed a moment ago)
  gtag("event", name, clean({ page_location: cleanUrl(), user_type: userType(), ...params }));
  relay(name, params);
};

// signed-in client: pseudonymous ids only (no names or emails)
export const identifyClient = (session) => {
  const memberId = session?.member?.id || session?.member?._id;
  const companyId = session?.company?.id || session?.company?._id;
  gtag("set", "user_properties", {
    user_type: userType(),
    ...(companyId && { company_id: String(companyId) }),
    ...(session?.company?.name && { company: String(session.company.name).slice(0, 36) }),
  });
  if (memberId) gtag("set", { user_id: String(memberId) });
};

// a category list / string from video data → one readable value
export const categoryOf = (vd) => {
  const c = vd?.category;
  if (Array.isArray(c)) return c.filter(Boolean).slice(0, 3).join(", ");
  return c || "";
};
